<script lang="ts">
  import type { ToolCallPart } from "../../chat/types";
  import { Loader2, ChevronRight, ShieldAlert } from "lucide-svelte";
  import MarkdownContent from "../../chat/markdown-content.svelte";
  import { getChatContext } from "../../chat/chat-runtime-context";
  import { i18n } from "../../chat/i18n.svelte";
  import { authStore } from "../../auth/auth-store";

  const { t } = i18n;
  const runtimeState = getChatContext().state;

  interface Props {
    part: ToolCallPart;
  }

  let { part }: Props = $props();
  let isExpanded = $state(false);

  function cleanResult(raw: string): string {
    try {
      const parsed = JSON.parse(raw);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return raw;
    }
  }

  const rawExplanation = $derived((part.args as any)?.explanation);
  const toolTitle = $derived(() => {
    if (rawExplanation != null && String(rawExplanation).trim() !== "") {
      return String(rawExplanation);
    }
    const name = part.name ?? "tool";
    const translated = (t("toolNames") as any)?.[name];
    if (translated) return translated;
    
    return name.replace(/_/g, " ");
  });
  const resultText = $derived(part.result ? cleanResult(part.result) : undefined);
  const isRunning = $derived(part.status === "pending" || part.status === "running");
  const argsJson = $derived(() => {
    const args = { ...(part.args ?? {}) };
    if ('explanation' in args) delete args.explanation;
    return JSON.stringify(args, null, 2);
  });

  // status color dot
  const statusColor = $derived(
    part.status === "complete" ? "var(--chat-success)" :
    part.status === "error" ? "var(--chat-error)" :
    "var(--chat-accent)"
  );
  
  const canShowDetails = $derived(
    $authStore.user?.isAdmin && $runtimeState.serverLlmInfo?.showToolDetails
  );
</script>

<div style="
  border: 1px solid var(--chat-bg-tertiary);
  border-radius: var(--chat-radius);
  overflow: hidden;
  background: var(--chat-bg-secondary);
  font-family: var(--chat-font-sans);
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
">
  <button
    type="button"
    onclick={() => (isExpanded = !isExpanded)}
    style="
      width: 100%;
      display: flex; align-items: center; gap: 7px;
      padding: 7px 10px;
      background: transparent;
      border: none; cursor: pointer;
      min-width: 0;
      transition: background 0.12s;
    "
    onmouseenter={(e) => ((e.currentTarget as HTMLElement).style.background = 'var(--chat-bg-tertiary)')}
    onmouseleave={(e) => ((e.currentTarget as HTMLElement).style.background = 'transparent')}
  >
    <ChevronRight size={13} style="
      flex-shrink: 0;
      color: var(--chat-accent);
      transition: transform 0.18s cubic-bezier(0.1, 0.9, 0.2, 1);
      transform: {isExpanded ? 'rotate(90deg)' : 'none'};
    " />

    <!-- Status indicator dot -->
    <div style="
      width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
      background: {statusColor};
      {isRunning ? 'animation: pulse 1.4s ease-in-out infinite;' : ''}
    "></div>

    <span style="
      flex: 1; min-width: 0;
      font-size: 12px; font-weight: 600;
      color: var(--chat-text-primary);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      text-align: left; text-transform: capitalize;
      letter-spacing: -0.01em;
    ">
      {toolTitle()}
    </span>

    {#if isRunning && part.status !== "awaiting_confirmation" && part.status !== "awaiting_selection"}
      <span class="thinking-loader-spin" style="flex-shrink: 0; color: var(--chat-accent);">
        <Loader2 size={11} />
      </span>
    {/if}
  </button>

  {#if part.status === "awaiting_selection"}
    <div style="
      padding: 10px 12px;
      background: var(--chat-accent-subtle);
      border-top: 1px solid var(--chat-bg-tertiary);
      display: flex; align-items: center; gap: 8px;
    ">
      <div style="
        width: 7px; height: 7px; border-radius: 50%;
        background: var(--chat-accent);
        animation: pulse 1.4s ease-in-out infinite;
      "></div>
      <span style="font-size: 11.5px; font-weight: 600; color: var(--chat-accent);">
        {t("awaitingSelection")}
      </span>
    </div>
  {/if}

  {#if part.status === "awaiting_confirmation"}
    <div style="
      padding: 10px 12px;
      background: var(--chat-accent-subtle);
      border-top: 1px solid var(--chat-bg-tertiary);
      display: flex; align-items: center; gap: 10px;
    ">
      <ShieldAlert size={14} style="color: var(--chat-accent); flex-shrink: 0;" />
      <span style="font-size: 11.5px; font-weight: 600; color: var(--chat-accent);">
        {t("awaitingApproval")}
      </span>
      {(() => { console.log(`[ToolCallBlock] 🛡️ Status: awaiting_confirmation for id=${part.id}`); return ''; })()}
    </div>
  {/if}

  {#if isExpanded}
    <div style="border-top: 1px solid var(--chat-border); background: var(--chat-bg);">
      {#if rawExplanation && String(rawExplanation).trim() !== ""}
        <div style="padding: 12px 16px; border-bottom: 1px solid var(--chat-bg-tertiary);">
          <div style="font-size: 11px; font-weight: 700; color: var(--chat-brand-green); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">{t("reasoning")}</div>
          <div style="font-size: 13px; line-height: 1.5; color: var(--chat-text-primary); max-height: 240px; overflow-y: auto;">
            <MarkdownContent text={String(rawExplanation)} />
          </div>
        </div>
      {/if}

      {#if canShowDetails}
        <div style="padding: 12px 16px;">
          <div style="font-size: 11px; font-weight: 700; color: var(--chat-brand-green); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">{t("arguments")}</div>
          <div style="max-height: 240px; overflow-y: auto; font-family: var(--chat-font-mono); font-size: 11px; line-height: 1.5; background: var(--chat-bg-tertiary); padding: 8px; border-radius: 6px;">
            <MarkdownContent text={`\`\`\`json\n${argsJson()}\n\`\`\``} />
          </div>
        </div>

        {#if resultText}
          <div style="padding: 12px 16px; border-top: 1px solid var(--chat-bg-tertiary);">
            <div style="font-size: 11px; font-weight: 700; color: var(--chat-brand-green); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;">
              {part.status === "error" ? t("error") : t("result")}
            </div>
            <div style="max-height: 240px; overflow-y: auto; font-family: var(--chat-font-mono); font-size: 11px; line-height: 1.5; background: var(--chat-bg-tertiary); padding: 8px; border-radius: 6px;">
              <MarkdownContent text={`\`\`\`json\n${resultText}\n\`\`\``} />
            </div>
          </div>
        {/if}
      {:else}
        <div style="padding: 10px 16px; border-top: 1px solid var(--chat-bg-tertiary);">
          <div style="font-size: 11px; font-weight: 400; color: var(--chat-text-muted); italic;">
            {t("detailsHidden")}
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>
